/**
 * Single source of truth for the admin / lab tech / member navigation
 * structure. Consumed by:
 *   - PortalShell (desktop dropdowns + mobile collapsible drawer)
 *   - CommandPalette (flattened list of navigable destinations)
 *
 * Each role returns `{ primary, settings }`:
 *   - `primary`: top-level links shown inline. Items may be flat (`{to,label}`)
 *     or a group with `children` (an array of `{to,label}` rendered as a
 *     dropdown on desktop / collapsible section on mobile).
 *   - `settings`: flat list rendered inside the right-hand "Settings"
 *     dropdown (desktop) / collapsible section (mobile).
 *
 * Keep this file declarative — no JSX, no React hooks. Components own all
 * presentation concerns.
 */
export function getNavStructure(role, user) {
  if (role === "partner") {
    // Aug 2026 — partner nav is capability-driven. PWS franchisees have
    // their own portal layout; this branch covers PortalShell-based pages
    // like /trade/retail so partners always have in-app navigation.
    const caps = user?.partner_capabilities || {};
    const isPws = user?.partner_type === "pws_franchisee";
    const primary = [];
    if (isPws) primary.push({ to: "/partner/dashboard", label: "Dashboard", testId: "partner-nav-dashboard" });
    if (caps.can_retail) primary.push({ to: "/trade/retail", label: "Retail Counter", testId: "partner-nav-retail" });
    if (caps.can_purchase_kits) primary.push({ to: "/partner/kit-orders", label: "Kit Orders", testId: "partner-nav-kit-orders" });
    return { primary, settings: [] };
  }
  if (role === "member") {
    return {
      primary: [
        { to: "/trade", label: "Dashboard" },
        { to: "/trade/orders", label: "Orders" },
        { to: "/trade/order", label: "Place Order" },
      ],
      settings: [],
    };
  }
  if (role === "lab_technician") {
    return {
      primary: [
        { to: "/lab/dashboard", label: "Dashboard", testId: "lab-nav-dashboard" },
        {
          label: "Bookings",
          testId: "admin-nav-bookings-group",
          children: [
            { to: "/admin/bookings", label: "Bookings List" },
            { to: "/admin/customers", label: "Customers" },
            { to: "/admin/properties", label: "Properties" },
          ],
        },
        {
          label: "Lab",
          testId: "admin-nav-lab-group",
          children: [
            { to: "/admin/lab", label: "Lab Queue" },
            { to: "/admin/lab/bulk", label: "Bulk Receive" },
          ],
        },
        {
          // Sep 2026 — Marketing module (Stage 3). Order is fixed:
          // Campaign Performance is the default Marketing page.
          label: "Marketing",
          testId: "admin-nav-marketing-group",
          children: [
            { to: "/admin/marketing/campaign-performance", label: "Campaign Performance", testId: "admin-nav-campaign-performance" },
            { to: "/admin/marketing/campaign-settings", label: "Campaign Settings", testId: "admin-nav-campaign-settings" },
            { to: "/admin/marketing/ad-spend", label: "Ad Spend", testId: "admin-nav-ad-spend" },
          ],
        },
        { to: "/trade", label: "Trade Partners", testId: "admin-nav-trade-partners" },
      ],
      settings: [],
    };
  }
  if (role === "admin") {
    // Jun 2026 — admin IA reorganised into 7 work-based groups. Nothing was
    // removed: every route that used to live in the flat 24-item Settings list
    // now sits in the group that matches how it's actually used, and Settings
    // keeps config-only pages split by `{ heading }` separators.
    return {
      primary: [
        {
          label: "Dashboard",
          testId: "admin-nav-dashboard-group",
          children: [
            { to: "/admin/dashboard", label: "Overview", testId: "admin-nav-dashboard" },
            { to: "/admin/response-dashboard", label: "Response Dashboard", testId: "admin-nav-response-dashboard" },
            { to: "/admin/analytics", label: "Monthly Performance", testId: "admin-nav-analytics" },
          ],
        },
        {
          label: "Bookings",
          testId: "admin-nav-bookings-group",
          children: [
            { to: "/admin/bookings", label: "Bookings List" },
            { to: "/admin/customers", label: "Customers" },
            { to: "/admin/properties", label: "Properties" },
            { to: "/admin/availability", label: "Availability", testId: "admin-nav-availability" },
            { to: "/admin/geo-routing", label: "GEO Scheduling", testId: "admin-nav-geo-routing" },
            { to: "/admin/discounts", label: "Discount Codes", testId: "admin-nav-discounts" },
            { to: "/admin/referral-invites", label: "Referral Invites", testId: "admin-nav-referral-invites" },
            { to: "/admin/commissions", label: "Commissions" },
          ],
        },
        {
          label: "Health Checks",
          testId: "admin-nav-health-checks-group",
          children: [
            { to: "/admin/tank-water-health-checks", label: "Tank Water Health Checks", testId: "admin-nav-tank-water-health-checks" },
            { to: "/field/today", label: "Field App · Today", testId: "admin-nav-field-today" },
            { to: "/field/technicians", label: "Field Technicians", testId: "admin-nav-field-technicians" },
            { to: "/admin/settings/servicem8", label: "ServiceM8 Scheduling", testId: "admin-nav-servicem8" },
          ],
        },
        {
          label: "Lab & Kits",
          testId: "admin-nav-lab-group",
          children: [
            { to: "/lab/dashboard", label: "Lab Dashboard", testId: "admin-nav-lab-dashboard" },
            { to: "/admin/lab", label: "Lab Queue" },
            { to: "/admin/lab/bulk", label: "Bulk Receive" },
            { to: "/admin/kits/today", label: "Kits · Today", testId: "admin-nav-kits-today" },
            { to: "/admin/bulk-kit-orders", label: "Bulk Kit Orders", testId: "admin-nav-pws-bulk-kit-orders" },
          ],
        },
        {
          label: "Sales",
          testId: "admin-nav-proposals-group",
          children: [
            { to: "/admin/proposals", label: "Estimates / Proposals", testId: "admin-nav-proposals" },
            { to: "/admin/proposal-presets", label: "Proposal Presets", testId: "admin-nav-proposal-presets" },
            { to: "/admin/quotes", label: "Pristine Quotes", testId: "admin-nav-quotes" },
            { to: "/admin/products", label: "Products & Services", testId: "admin-nav-products" },
            { to: "/admin/retail-invoices", label: "Retail Invoices", testId: "admin-nav-retail-invoices" },
            { to: "/admin/carts", label: "Carts" },
          ],
        },
        {
          // Sep 2026 — Marketing module (Stage 3). Order is fixed:
          // Campaign Performance is the default Marketing page.
          label: "Marketing",
          testId: "admin-nav-marketing-group",
          children: [
            { to: "/admin/marketing/campaign-performance", label: "Campaign Performance", testId: "admin-nav-campaign-performance" },
            { to: "/admin/marketing/campaign-settings", label: "Campaign Settings", testId: "admin-nav-campaign-settings" },
            { to: "/admin/marketing/ad-spend", label: "Ad Spend", testId: "admin-nav-ad-spend" },
            { to: "/admin/property-campaigns", label: "Property Campaigns", testId: "admin-nav-property-campaigns" },
            { to: "/admin/postcard-studio", label: "Postcard Studio", testId: "admin-nav-postcard-studio" },
            { to: "/admin/partner-broadcast", label: "Partner Broadcast", testId: "admin-nav-pws-broadcast" },
            { to: "/admin/attribution", label: "Revenue" },
            { to: "/admin/tools/attribution", label: "Attribution tools", testId: "admin-nav-attr-tools" },
            { to: "/admin/reports/post-test-service-revenue", label: "Post-Test Service Revenue", testId: "admin-nav-pws-sm8-revenue" },
          ],
        },
        {
          // Jun 2026 — the two networks (trade referrals vs. PWS franchisees)
          // now share one menu, separated by headings so they can't be
          // confused. The full 20+ page PWS directory still lives on the
          // PWS Overview hub — this curates the most-used surfaces.
          label: "Partners",
          testId: "admin-nav-partners-group",
          children: [
            { heading: "Trade Partners" },
            { to: "/admin/trade-hub", label: "Partner Hub", testId: "admin-nav-trade-hub" },
            { to: "/trade", label: "Trade Partners", testId: "admin-nav-trade-partners" },
            { to: "/admin/partners", label: "Partners", testId: "admin-nav-partners" },
            { to: "/admin/partner-prospects", label: "Prospects", testId: "admin-nav-partner-prospects" },
            { to: "/admin/partner-acquisition", label: "Acquisition", testId: "admin-nav-partner-acquisition" },
            { to: "/admin/transfers", label: "Transfers" },
            { to: "/admin/memberships", label: "Members+" },
            { heading: "PWS Franchisees" },
            { to: "/admin/partners/pws-overview",   label: "PWS Overview",           testId: "admin-nav-pws-overview" },
            { to: "/admin/partners?partner_type=pws_franchisee", label: "Franchisee Directory", testId: "admin-nav-pws-directory" },
            { to: "/admin/pws-franchisee-interest", label: "Applications",           testId: "admin-nav-pws-applications" },
            { to: "/admin/pws-partner-landing",     label: "Landing Pages",          testId: "admin-nav-pws-landing" },
            { to: "/admin/partners/network",        label: "Network Status",         testId: "admin-nav-pws-network-status" },
            { to: "/admin/kit-reimbursements",             label: "Kit Reimbursements",     testId: "admin-nav-pws-kit-reimbursements" },
            { to: "/admin/kit-reimbursement-payout-runs",  label: "Kit Reimb Payout Runs",  testId: "admin-nav-pws-kit-reimb-payout-runs" },
            { to: "/admin/partners/marketing-kit",  label: "Marketing Kit",          testId: "admin-nav-pws-marketing" },
            { to: "/admin/partners/payouts",        label: "Payouts",                testId: "admin-nav-pws-payouts" },
            { to: "/admin/partners/commissions",    label: "Commissions",            testId: "admin-nav-pws-commissions" },
            { to: "/admin/partners/activity-controls", label: "Activity Controls",  testId: "admin-nav-pws-activity" },
            { to: "/pws-franchisee-network",        label: "Public Network Page ↗",  testId: "admin-nav-pws-public-network" },
            { to: "/admin/partners/pws-presentation", label: "Franchisee Presentation", testId: "admin-nav-pws-presentation" },
            { to: "/pws-partner/guide",               label: "Partner Guide (flipbook) ↗", testId: "admin-nav-pws-partner-guide" },
          ],
        },
      ],
      settings: [
        { to: "/admin/settings", label: "All Settings", testId: "admin-nav-settings-hub" },
        { heading: "Pricing" },
        { to: "/admin/pricing/test-catalog", label: "Test Catalog Pricing", testId: "admin-nav-test-catalog" },
        { to: "/admin/pricing/partner-types", label: "Partner Pricing", testId: "admin-nav-partner-pricing" },
        { to: "/admin/settings/online-discounts", label: "Online Discount Control", testId: "admin-nav-online-discounts" },
        { heading: "Integrations" },
        { to: "/admin/stripe", label: "Stripe" },
        { to: "/admin/settings/xero-upload", label: "Xero Upload", testId: "admin-nav-xero-upload" },
        { to: "/admin/emails", label: "Emails" },
        { to: "/admin/inbound-email/unmatched", label: "Unmatched replies", testId: "admin-nav-unmatched-emails" },
        { heading: "Platform" },
        { to: "/admin/settings/features", label: "Features", testId: "admin-nav-features" },
        { to: "/admin/settings/posted-kit", label: "Posted Kit", testId: "admin-nav-posted-kit" },
        { to: "/admin/settings/bulk-kit", label: "Bulk Kits", testId: "admin-nav-bulk-kit" },
        { to: "/admin/settings/booking-availability", label: "Booking Availability", testId: "admin-nav-booking-availability" },
        { to: "/admin/settings/new-booking-notifications", label: "New Booking Notifications", testId: "admin-nav-new-booking-notifications" },
        { to: "/admin/recommendations", label: "Rules" },
        { to: "/admin/users", label: "Members" },
        { to: "/admin/readiness", label: "Readiness" },
      ],
    };
  }
  return { primary: [], settings: [] };
}

/**
 * Entries shaped `{ heading }` (no `to`) are non-navigable separators rendered
 * as a small uppercase label inside a dropdown / drawer section.
 *
 * Flatten a `primary` array into a single list of navigable `{to,label,...}`
 * rows. Group items are expanded into their children with the group label
 * prepended ("Group · Child"). Items without `to` are dropped.
 *
 * Used by CommandPalette to render every destination as a single row, and by
 * PortalShell for "any link active" detection across grouped items.
 */
export function flattenPrimary(items) {
  const out = [];
  for (const item of items || []) {
    if (Array.isArray(item.children) && item.children.length) {
      for (const child of item.children) {
        if (child && child.to) out.push({ ...child, label: `${item.label} · ${child.label}` });
      }
    } else if (item && item.to) {
      out.push(item);
    }
  }
  return out;
}
